import {
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError as observableThrowError, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';
import { AuthenticationApiModels } from '../../../../models/api/authentication-api.models';
import { AuthenticationModels } from '../../../../models/authentication.models';
import { ConfigurationModels } from '../../../../models/configuration.models';
import { ErrorModels } from '../../../../models/error.model';
import { AuthenticationServiceBase } from './authentication-service-base';
import { InteractionService } from './interaction.service';
import { LoggerService } from './logger/logger.service';
import { ResourcesService } from './resources/resources.service';

@Injectable({
  providedIn: 'root',
})
export class HttpTunnelSanteInterceptor implements HttpInterceptor {
  private router!: Router;
  private authService!: AuthenticationServiceBase<
    AuthenticationModels.Account,
    AuthenticationApiModels.AccountInfoResponse
  >;
  private config!: ConfigurationModels.Configuration;

  constructor(
    private injector: Injector,
    private logService: LoggerService,
    private resourcesService: ResourcesService,
    private loggerService: LoggerService,
    private interactionService: InteractionService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    // Get Router with inject due to issue with HttpInterceptor : https://github.com/angular/angular/issues/18224
    this.router = this.injector.get(Router);
    this.authService = this.injector.get(AuthenticationServiceBase);
    this.config = this.injector.get(ConfigurationModels.Configuration);
    let httpClient = this.injector.get(HttpClient);
    let token = this.authService.getAccessToken();
    let tokenData = sessionStorage.getItem('tokenData');

    let headers = req.headers
      .set('Cache-Control', 'no-cache')
      .set('Pragma', 'no-cache')
      .set('Expires', 'Sat, 01 Jan 2000 00:00:00 GMT');

    if (req.responseType === 'json' && tokenData) {
      headers = headers.set('MTX-Data-Token', `${tokenData}`);
    }

    let withHeaders = req.clone({
      headers: headers,
    });

    // TODO : Catch -> pipe(catchError)
    return next.handle(withHeaders).pipe(
      switchMap((info: any) => {
        //this.loggerService.log(info);
        if (info instanceof HttpResponse) {
          if (info.body && info.body.statusCode === 406) {
            this.router.navigate(['/demande-devis']);
            return of(info);
          }
        }
        return of(info);
      }),
      catchError((err: any) => {
        if (err instanceof HttpErrorResponse) {
          if (req.url === `${this.config.apiRootUrl}/account/renew`) {
            this.authService.logoff();
            // Go login
            this.router.navigate(['/login']);
            return observableThrowError(<ErrorModels.TunnelSanteError>{
              code: 401,
              apiMessage: 'disconnected from server',
              appMessage: this.resourcesService.getErrorResource(401),
              innerError: err,
            });
          }

          if (
            err instanceof HttpErrorResponse &&
            err.error instanceof Blob &&
            err.error.type === 'application/json'
          ) {
            return new Promise<any>((_resolve, reject) => {
              let reader = new FileReader();
              reader.onload = (e: Event) => {
                try {
                  const errmsg = JSON.parse((<any>e.target).result);
                  this.interactionService.showError(
                    errmsg.message,
                    this.resourcesService.getErrorResource(errmsg.code),
                  );
                  reject(<ErrorModels.TunnelSanteQuotationError>{
                    code: errmsg.code,
                    apiMessage: errmsg.message,
                    appMessage: this.resourcesService.getErrorResource(
                      errmsg.code,
                    ),
                    details: errmsg.details,
                    innerError: err,
                  });
                } catch (e) {
                  reject(err);
                }
              };
              reader.onerror = (_e) => {
                reject(err);
              };
              reader.readAsText(err.error);
            });
          }

          if (err.status === 0) {
            this.interactionService.showError(
              'server unreachable',
              this.resourcesService.getErrorResource(1),
            );
            return observableThrowError(<ErrorModels.TunnelSanteError>{
              code: 1,
              apiMessage: 'server unreachable',
              appMessage: this.resourcesService.getErrorResource(1),
            });
          }

          if (
            err.status === 401 &&
            req.url.startsWith(this.config.apiRootUrl) &&
            !req.url.endsWith('login') &&
            !(<any>req).isReplay
          ) {
            return this.authService.renewToken().pipe(
              mergeMap((newAccessToken) => {
                let replayHeaders = headers.set(
                  'Authorization',
                  `Bearer ${newAccessToken}`,
                );
                let replayReq = req.clone({
                  headers: replayHeaders,
                });

                (<any>replayReq).isReplay = true;
                return httpClient.request(replayReq);
              }),
            );
          } else if (err.error && err.error.code) {
            if (err.error.details && err.error.code === 5000) {
              this.interactionService.showError(
                err.error.message,
                this.resourcesService.getErrorResource(err.error.code),
              );
              return observableThrowError(<
                ErrorModels.TunnelSanteQuotationError
              >{
                code: err.error.code,
                apiMessage: err.error.message,
                appMessage: this.resourcesService.getErrorResource(
                  err.error.code,
                ),
                details: err.error.details,
                innerError: err,
              });
            }

            this.interactionService.showError(
              err.error.message,
              this.resourcesService.getErrorResource(err.error.code),
            );
            return observableThrowError(<ErrorModels.TunnelSanteError>{
              code: err.error.code,
              apiMessage: err.error.message,
              appMessage: this.resourcesService.getErrorResource(
                err.error.code,
              ),
              innerError: err,
            });
          }
        }

        this.logService.error(err);
        this.interactionService.showError(
          '',
          this.resourcesService.getErrorResource(-1),
        );
        return observableThrowError(<ErrorModels.TunnelSanteError>{
          code: -1,
          apiMessage: undefined,
          appMessage: this.resourcesService.getErrorResource(-1),
          innerError: err,
        });
      }),
    );
  }
}
